import { Chip, Container, HeaderBar, Tabs, TypoGraph } from 'components'
import { useMemo, useState } from 'react'
import { fleetReportingTabs } from 'constants/status'
import styles from './fleetReporting.module.scss'
import FleetPercentiles from './fleetPercentiles'
import FleetDetailsForAgents from 'pages/carriers/partial/fleetDetailsForAgent'
import FleetMilesByZipDashboard from 'pages/carriers/partial/fleetMilesByZip'
import moment from 'moment'
import SafetyEventsDashboard from 'pages/carriers/partial/safetyEvents'
import HeartbeatDashboard from 'pages/carriers/partial/heartbeat'
import DriversDashboard from 'pages/carriers/partial/drivers'
import CarrierAndNBAgentsDetailsTab from 'pages/carriers/partial/nbDetails/carrierAndAgentNbDetails'
import MileageReportingDashboard from 'pages/reporting/partial/mileageReporting'
import LocationReportingDashboard from 'pages/reporting/partial/locationReporting'
import RiskDecileDashboard from 'pages/reporting/partial/riskDecile'
import UbiSummaryDashboard from 'pages/carriers/partial/ubiSummary'
import { isCarrierLogin } from 'tools/auth'
import { useCarrierCodeBootstrap } from 'hooks/useCarrierCodeBootstrap'
import { REPORT_KEYS, getReportFlagKey } from 'constants/reporting'
import { useFeatureFlag } from 'contexts/ReportingContext'
import strings from 'strings'

const TABS = strings.CARRIERS.TABS

const fleetReportingTabsForCarrier = [
  { label: TABS.FLEET_HEARTBEAT },
  { label: TABS.FLEET_DETAILS },
  { label: TABS.FLEET_MILES_BY_ZIP },
  { label: TABS.SAFETY_EVENTS },
  { label: TABS.DRIVERS },
  { label: TABS.MILEAGE_REPORTING },
  { label: TABS.LOCATION_REPORTING },
  { label: TABS.RISK_DECILE },
]

interface IFleetDetailProps { }

const FleetReporting: React.FC<IFleetDetailProps> = (props) => {
    const [fleetReportingTab, setFleetReportingTab] = useState(0)
    const reportingTitle = 'Fleet Reporting'
    const isCarrier = isCarrierLogin()

    // UBI Summary is offered to carriers only when its feature flag is on. The flag is an
    // insurer-level setting: carrier-api resolves it against the insurer that holds this carrier
    // (found via the CEA), NOT the carrier's own company. Prepend it as the first carrier tab —
    // matching its position in the insurer's carrier-detail view.
    const ubiEnabled = useFeatureFlag(getReportFlagKey(REPORT_KEYS.UBI_SUMMARY))
    const carrierTabs = useMemo(
        () => [
            ...(ubiEnabled ? [{ label: TABS.UBI_SUMMARY }] : []),
            ...fleetReportingTabsForCarrier,
        ],
        [ubiEnabled]
    )
    const tabs = isCarrier ? carrierTabs : fleetReportingTabs

    useCarrierCodeBootstrap()

    let riskEldObject: any = {}

    const TabPanel = (props: { print: boolean }) => {
        const { print } = props

        if (isCarrier) {
            // Dispatch by label, not index: the UBI tab shifts the positions and only exists
            // when the flag is on (and it resolves async), so a fixed index→panel map desyncs.
            switch (carrierTabs[fleetReportingTab]?.label) {
                case TABS.UBI_SUMMARY:
                    return <UbiSummaryDashboard />
                case TABS.FLEET_HEARTBEAT:
                    return <HeartbeatDashboard />
                case TABS.FLEET_DETAILS:
                    return <CarrierAndNBAgentsDetailsTab useKilometers={false} />
                case TABS.FLEET_MILES_BY_ZIP:
                    return <FleetMilesByZipDashboard print={print} useCarrierFilters={false} />
                case TABS.SAFETY_EVENTS:
                    return <SafetyEventsDashboard print={print} useCarrierFilters={true} />
                case TABS.DRIVERS:
                    return <DriversDashboard useKilometers={false} />
                case TABS.MILEAGE_REPORTING:
                    return <MileageReportingDashboard print={print} useCarrierFilters={true} />
                case TABS.LOCATION_REPORTING:
                    return <LocationReportingDashboard print={print} useCarrierFilters={true} />
                case TABS.RISK_DECILE:
                    return <RiskDecileDashboard useCarrierFilters={true} />
                default:
                    return <HeartbeatDashboard />
            }
        }

        switch (fleetReportingTab) {
            case 0:
                return <HeartbeatDashboard  />
            case 1:
                return <FleetPercentiles print={print} />
            case 2:
                return <FleetDetailsForAgents print={print} />
            case 3:
                return <FleetMilesByZipDashboard print={print} useCarrierFilters={false} />
            case 4:
                return <SafetyEventsDashboard print={print} useCarrierFilters={true} />
            default:
                return <HeartbeatDashboard  />
        }
    }

    const getPageMargins = () => {
        return `@page { margin: 30px!important; }`
    }

    return (
        <Container className={styles.fleetReportingContainer}>
            <Container className={styles.headerContainer}>
                <Container className={styles.headerTitleContainer}>
                    <TypoGraph
                        variant="h1"
                        sx={{ marginRight: '1rem' }}
                        mb={0}
                        content={reportingTitle}
                    />
                    <Chip
                        label={`Updated ${moment(riskEldObject?.updated).format('dddd, MMMM Do')}`}
                        variant="filled"
                        sx={{
                            backgroundColor: '#E1FAEB',
                            color: '#20693D',
                        }}
                    />
                </Container>
            </Container>
            <Tabs
                tabData={tabs}
                value={fleetReportingTab}
                id={tabs[fleetReportingTab].label}
                handleChange={(_e, val) => setFleetReportingTab(val)}
                sxContainer={{
                    marginLeft: '1rem',
                }}
            />
            <Container>
                <TabPanel print={false} />
            </Container>
            <style>{getPageMargins()}</style>
        </Container>
    )
}

export default FleetReporting
